import { useNotificationStore } from "@/states/zustand/notification";
import { Form, FormInstance, FormProps, Image, Input } from "antd";
import Upload, { UploadChangeParam, UploadFile } from "antd/es/upload";
import { useEffect, useState } from "react";
import { AiOutlineUpload } from "react-icons/ai";

export type FormValues = {
    accName: string;
    accNo: string;
    paymentType: string;
    published?: boolean;
    image?: UploadChangeParam<UploadFile>;
}

type PaymentFormProps = {
    form: FormInstance;
    initialValues?: Partial<Payment>;
    onSubmit: (values: FormValues) => void;
    openedModal: boolean // to control the image reset based on modal opening state
} & Omit<FormProps, 'form' | 'initialValues' | 'onFinish'>;

export function PaymentForm({
    form,
    initialValues,
    onSubmit,
    openedModal,
    ...props
}: PaymentFormProps) {

    const [image, setImage] = useState<{ file: UploadFile | null, url: string } | null>({
        file: null,
        url: initialValues?.imageUrl ?? ""
    });

    const onFinish = (values: FormValues) => {
        onSubmit(values);
    };

    useEffect(() => {
        if (openedModal) {
            setImage({
                file: null,
                url: initialValues?.imageUrl ?? ""
            });
        }
    }, [openedModal])

    const onImageChange = ({ file }: UploadChangeParam<UploadFile>) => {
        // check if the file is too large
        if (file.size && file.size > 2 * 1024 * 1024) { // 2MB limit
            useNotificationStore.getState().setNotification({
                type: "warning",
                message: "Image size exceeds 2MB limit. Please upload a smaller image."
            });
            return;
        }

        // check if file has an originFileObj or not
        const fileObj = file?.originFileObj as File ?? file as unknown as File;
        const url = URL.createObjectURL(fileObj);

        setImage({
            file,
            url
        });
    };

    return (
        <Form
            onFinish={onFinish}
            form={form}
            layout="vertical"
            initialValues={initialValues}
            {...props}
            className="!pt-4"
        >
            <Form.Item
                label="Payment Name"
                name={"paymentType"}
                rules={[{
                    required: true,
                    message: "Please enter the payment name!"
                }]}
            >
                <Input
                    placeholder="Payment Name"
                    size="large"
                />
            </Form.Item>

            <Form.Item
                label="Account Name / Payment Username"
                name={"accName"}
                rules={[{
                    required: true,
                    message: "Please enter the account name!"
                }]}
            >
                <Input
                    placeholder="Account Name"
                    size="large"
                />
            </Form.Item>

            <Form.Item
                label="Account Number / Payment Mobile Number"
                name={"accNo"}
                rules={[
                    {
                        required: true,
                        message: "Please enter the account number!"
                    },
                    {
                        pattern: /^(09\d{6,9}|\d{16})$/,
                        message: "Invalid account number information!"
                    }
                ]}
            >
                <Input
                    placeholder="Account Number"
                    size="large"
                    maxLength={21}
                />
            </Form.Item>

            <Form.Item
                label="Payment Image"
                name={"image"}
                rules={[{
                    required: true,
                    message: "Please upload the payment image!"
                }]}
            >
                <Upload
                    accept="image/*"
                    maxCount={1}
                    beforeUpload={(file) => {
                        if (file.size > 2 * 1024 * 1024) { // 2MB limit
                            useNotificationStore.getState().setNotification({
                                type: "warning",
                                message: "Image size exceeds 2MB limit. Please upload a smaller image."
                            });
                            // return Upload.LIST_IGNORE;
                        }
                        return false;
                    }} // Prevent automatic upload
                    listType="picture-card"
                    showUploadList={false}
                    onChange={onImageChange}
                >
                    {
                        image && image?.url ? (
                            <Image
                                src={image.url}
                                alt="Payment Image"
                                width={100}
                                height={100}
                                className="rounded-sm object-cover "
                                preview={false}
                            />
                        ) : (
                            <AiOutlineUpload
                                size={32}
                            />
                        )
                    }
                </Upload>
            </Form.Item>
        </Form>
    )
}
